import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Global axios interceptor: always attach session token from localStorage as fallback
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('bookeasy_session');
  if (token && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const headers = {};
      const savedToken = localStorage.getItem('bookeasy_session');
      if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true, headers });
      setUser(response.data);
    } catch {
      setUser(null);
      localStorage.removeItem('bookeasy_session');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Save current page so user returns here after login (e.g., booking page)
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/dashboard') {
      sessionStorage.setItem('bookeasy_return_to', currentPath);
    }
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      const headers = {};
      const savedToken = localStorage.getItem('bookeasy_session');
      if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true, headers });
    } catch {}
    localStorage.removeItem('bookeasy_session');
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
